<template>
	<div>
		<div class="text-center py-2" v-if="loading > 0">
			<b-spinner small></b-spinner>
		</div>
		<b-alert variant="danger" :show="!!error">{{error && (error.error || error.message || error.toString())}}</b-alert>

		<div v-for="ch in channels" :key="`${ch.service_id}-${ch.token}`" class="mb-4">
			<div class="d-flex align-items-center mb-3">
				<div class="position-relative mr-2 flex-shrink-0">
					<b-avatar :src="ch.avatar_url || serviceIcons[ch.service_id]" size="2.4em"></b-avatar>
					<img v-if="serviceIcons[ch.service_id]" :src="serviceIcons[ch.service_id]" :title="ch.service_name" class="service-badge">
				</div>
				<div class="overflow-hidden">
					<div class="text-truncate">{{ch.title}}</div>
					<div class="small text-muted text-truncate">{{ch.service_name}}</div>
				</div>
			</div>

			<b-form-group v-for="(field, key) in ch.fields" :key="key"
				:label="field.type === 'bool' ? null : (field.label || key)" :description="field.help">

				<!-- boolean -->
				<b-form-checkbox v-if="field.type === 'bool'" switch v-model="model[ck(ch)][key]">{{field.label || key}}</b-form-checkbox>

				<!-- integer -->
				<b-form-input v-else-if="field.type === 'int'" type="number" v-model.number="model[ck(ch)][key]"></b-form-input>

				<!-- static select -->
				<b-form-select v-else-if="field.type === 'select' && !field.dynamic" v-model="model[ck(ch)][key]" :options="normalizeOptions(field.options)">
					<template #first><b-form-select-option :value="null">— none —</b-form-select-option></template>
				</b-form-select>

				<!-- dynamic select (options fetched via getFormData) -->
				<b-form-select v-else-if="field.type === 'select' && field.dynamic" v-model="model[ck(ch)][key]"
					:options="dynamicOpts[`${ck(ch)}:${key}`] || []" :disabled="!!optsLoading[`${ck(ch)}:${key}`]">
					<template #first><b-form-select-option :value="null">{{ optsLoading[`${ck(ch)}:${key}`] ? 'Loading…' : '— none —' }}</b-form-select-option></template>
				</b-form-select>

				<!-- multi-select: static options from schema, or dynamic via getFormData -->
				<template v-else-if="field.type === 'multiselect'">
					<b-form-checkbox-group v-if="!field.dynamic" v-model="model[ck(ch)][key]" :options="normalizeOptions(field.options)" stacked></b-form-checkbox-group>
					<template v-else>
						<div v-if="optsLoading[`${ck(ch)}:${key}`]" class="text-muted small"><b-spinner small></b-spinner> Loading…</div>
						<b-form-checkbox-group v-else-if="(dynamicOpts[`${ck(ch)}:${key}`] || []).length" v-model="model[ck(ch)][key]" :options="dynamicOpts[`${ck(ch)}:${key}`]" stacked></b-form-checkbox-group>
						<div v-else class="text-muted small">No options available.</div>
					</template>
				</template>

				<!-- autocomplete -->
				<div v-else-if="field.type === 'autocomplete'" class="autocomplete-wrap">
					<b-input-group>
						<b-form-input :value="acText[`${ck(ch)}:${key}`]" autocomplete="off"
							@input="onAcInput(ch.service_id, ck(ch), key, $event)" @focus="acOpen = `${ck(ch)}:${key}`"
							@blur="onAcBlur" placeholder="Type to search…"></b-form-input>
						<b-input-group-append is-text v-if="optsLoading[`${ck(ch)}:${key}`]"><b-spinner small></b-spinner></b-input-group-append>
					</b-input-group>
					<b-list-group v-if="acOpen === `${ck(ch)}:${key}` && (acSuggestions[`${ck(ch)}:${key}`] || []).length" class="autocomplete-list">
						<b-list-group-item button v-for="(opt, i) in acSuggestions[`${ck(ch)}:${key}`]" :key="i" class="py-1"
							@mousedown.prevent="selectAc(ck(ch), key, opt)">{{opt.name}}</b-list-group-item>
					</b-list-group>
				</div>

				<!-- text / fallback -->
				<b-form-input v-else v-model="model[ck(ch)][key]"></b-form-input>
			</b-form-group>
		</div>

		<b-alert variant="info" :show="loading === 0 && !error && !channels.length" class="mb-0">
			No platform-specific options - connect channels on platforms that support them (e.g. YouTube).
		</b-alert>
	</div>
</template>

<script>
import debounce from 'debounce';

export default {
	name: 'ServiceFieldsEditor',
	props: {
		serviceIcons: { type: Object, default: () => ({}) },
	},
	data() { return {
		loading: 0,
		error: null,
		channels: [],
		model: {},
		dynamicOpts: {},
		optsLoading: {},
		acText: {},
		acSuggestions: {},
		acOpen: null,
	}},
	mounted() {
		this.load();
	},
	methods: {
		// Per-channel key (one set of settings per channel).
		ck(ch) {
			return `${ch.service_id}:${ch.token}`;
		},
		// Per-channel settings to persist: [{ service_id, token, settings }].
		getSettings() {
			return this.channels.map(ch => ({ service_id: ch.service_id, token: ch.token, settings: this.model[this.ck(ch)] || {} }));
		},
		normalizeOptions(options) {
			return (options || []).map(o => (typeof o === 'object' ? o : { value: o, text: o }));
		},
		async load() {
			try {
				this.loading++;
				this.error = null;
				this.channels = await this.$api.get('serviceFields');
				const model = {};
				for(const ch of this.channels) {
					const key = this.ck(ch);
					const m = { ...(ch.values || {}) };
					for(const [fkey, field] of Object.entries(ch.fields)) {
						if(field.type === 'bool' && m[fkey] == null) m[fkey] = false;
						else if(field.type === 'multiselect' && m[fkey] == null) m[fkey] = [];
						else if(field.type === 'select' && !field.dynamic && m[fkey] == null && field.default != null) m[fkey] = field.default;
						else if((field.type === 'autocomplete' || (field.type === 'select' && field.dynamic)) && m[fkey] == null && fkey === 'category' && ch.categoryPrefill)
							m[fkey] = { token: ch.categoryPrefill.token, name: ch.categoryPrefill.name };
					}
					model[key] = m;
				}
				this.model = model;
				// Init autocomplete text + load dynamic select options.
				for(const ch of this.channels) {
					const key = this.ck(ch);
					for(const [fkey, field] of Object.entries(ch.fields)) {
						const k = `${key}:${fkey}`;
						if(field.type === 'autocomplete')
							this.$set(this.acText, k, this.model[key][fkey]?.name || '');
						if(field.dynamic && (field.type === 'select' || field.type === 'multiselect'))
							this.loadDynamicOptions(ch.service_id, key, fkey);
					}
				}
			} catch(e) {
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		async loadDynamicOptions(serviceId, chKey, field) {
			const k = `${chKey}:${field}`;
			try {
				this.$set(this.optsLoading, k, true);
				const rows = await this.$api.get(`serviceFormData/${encodeURIComponent(serviceId)}/${encodeURIComponent(field)}`);
				const opts = (rows || []).map(o => ({ value: { token: o.token, name: o.name }, text: o.name }));
				this.$set(this.dynamicOpts, k, opts);
				// Re-point current value(s) at the matching option object(s) so they show as selected.
				const cur = this.model[chKey][field];
				if(Array.isArray(cur)) {
					this.$set(this.model[chKey], field, cur.map(v => {
						const match = opts.find(o => String(o.value.token) === String(v?.token));
						return match ? match.value : v;
					}));
				} else if(cur && cur.token != null) {
					const match = opts.find(o => String(o.value.token) === String(cur.token));
					if(match) this.$set(this.model[chKey], field, match.value);
				}
			} catch(e) {
				this.$set(this.dynamicOpts, k, []);
			} finally {
				this.$set(this.optsLoading, k, false);
			}
		},
		onAcInput(serviceId, chKey, field, value) {
			const k = `${chKey}:${field}`;
			this.$set(this.acText, k, value);
			this.acOpen = k;
			this._acFetch(serviceId, chKey, field, value);
		},
		_acFetch: debounce(function(serviceId, chKey, field, query) {
			const k = `${chKey}:${field}`;
			this.$set(this.optsLoading, k, true);
			this.$api.get(`serviceFormData/${encodeURIComponent(serviceId)}/${encodeURIComponent(field)}`, { q: query })
				.then(rows => { this.$set(this.acSuggestions, k, rows || []); })
				.catch(() => { this.$set(this.acSuggestions, k, []); })
				.finally(() => { this.$set(this.optsLoading, k, false); });
		}, 300),
		selectAc(chKey, field, opt) {
			const k = `${chKey}:${field}`;
			this.$set(this.model[chKey], field, { token: opt.token, name: opt.name });
			this.$set(this.acText, k, opt.name);
			this.$set(this.acSuggestions, k, []);
			this.acOpen = null;
		},
		onAcBlur() {
			// Delay so a suggestion mousedown registers before closing.
			setTimeout(() => { this.acOpen = null; }, 150);
		},
	},
};
</script>

<style lang="scss">
/* Service icon overlaid on the channel avatar - no background, no clipping. */
.service-badge {
	position: absolute;
	right: -0.35em;
	bottom: -0.35em;
	width: 1.6em;
	height: 1.6em;
	object-fit: contain;
}
.autocomplete-wrap {
	position: relative;
}
.autocomplete-list {
	position: absolute;
	z-index: 1000;
	width: 100%;
	max-height: 220px;
	overflow-y: auto;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
